import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Clock, UserMinus, Users, Music, Search, X, AlertCircle } from "lucide-react";
import { Performer } from "@shared/schema";
import { formatTimer } from "@/lib/timer-utils";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import { MutationButton } from "@/components/mutation-button";

interface SubstituteOutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  performerToSubOut: Performer;
  availableReplacements: Performer[];
  onConfirm: (replacementPerformerId?: number, selectedInstruments?: string[]) => void;
}

export default function SubstituteOutModal({
  open,
  onOpenChange,
  performerToSubOut,
  availableReplacements,
  onConfirm,
}: SubstituteOutModalProps) {
  const [selectedReplacementId, setSelectedReplacementId] = useState<string>("");
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [hasManuallyModifiedInstruments, setHasManuallyModifiedInstruments] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  // Detect Chrome browsers for different transparency treatment
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Filter available replacements to exclude those already on stage, pending stage, or removed
  const eligibleReplacements = availableReplacements.filter(
    (performer) => performer.status === 'participant' && !performer.removed
  );

  // Filter and sort replacements based on search term
  const filteredReplacements = (() => {
    let filtered = eligibleReplacements;
    
    // Only filter if there's a search term
    if (searchTerm.trim().length > 0) {
      filtered = eligibleReplacements.filter(performer => 
        performer.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  })();


  // Get selected replacement performer
  const selectedReplacement = selectedReplacementId ? 
    eligibleReplacements.find(p => p.id === parseInt(selectedReplacementId)) : null;

  // Auto-select instruments when replacement changes (but not when user has manually modified)
  useEffect(() => {
    if (selectedReplacementId && !hasManuallyModifiedInstruments) {
      const replacement = eligibleReplacements.find(p => p.id === parseInt(selectedReplacementId));
      if (replacement) {
        let newInstruments: string[] = [];
        if (replacement.instruments?.length === 1) {
          newInstruments = replacement.instruments;
        } else if (replacement.instruments?.length > 1) {
          const overlappingInstruments = replacement.instruments.filter(
            instrument => performerToSubOut.currentInstruments?.includes(instrument)
          );
          newInstruments = overlappingInstruments.length > 0 ? [overlappingInstruments[0]] : [];
        }
        // Only update if different to prevent unnecessary re-renders
        if (JSON.stringify(newInstruments) !== JSON.stringify(selectedInstruments)) {
          setSelectedInstruments(newInstruments);
        }
      }
    } else if (!selectedReplacementId) {
      // When no replacement is selected, clear instruments and reset manual flag
      if (selectedInstruments.length > 0) {
        setSelectedInstruments([]);
      }
      if (hasManuallyModifiedInstruments) {
        setHasManuallyModifiedInstruments(false);
      }
    }
  }, [selectedReplacementId, eligibleReplacements, performerToSubOut.currentInstruments]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true); // Always show dropdown when typing
  };

  const handleSelectReplacement = (performer: Performer) => {
    setSelectedReplacementId(performer.id.toString());
    setSearchTerm(performer.name);
    setShowDropdown(false);
    setHasManuallyModifiedInstruments(false);
  };

  const handleClearSelection = () => {
    setSelectedReplacementId("");
    setSearchTerm("");
    setShowDropdown(false);
    setHasManuallyModifiedInstruments(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const handleConfirm = () => {
    const replacementId = selectedReplacementId ? parseInt(selectedReplacementId) : undefined;
    
    // Validation: if there's a replacement with multiple instruments, ensure one is selected
    if (replacementId && selectedReplacement && selectedReplacement.instruments && selectedReplacement.instruments.length > 1) {
      if (selectedInstruments.length === 0) {
        return; // Don't proceed if no instrument is selected
      }
    }
    
    onConfirm(replacementId, replacementId ? selectedInstruments : undefined);
    setSelectedReplacementId("");
    setSelectedInstruments([]);
    setHasManuallyModifiedInstruments(false);
    setSearchTerm("");
    setShowDropdown(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedReplacementId("");
    setSelectedInstruments([]);
    setHasManuallyModifiedInstruments(false);
    setSearchTerm("");
    setShowDropdown(false);
    onOpenChange(false);
  };

  const handleInstrumentSelect = (instrument: string) => {
    setHasManuallyModifiedInstruments(true);
    setSelectedInstruments([instrument]); // Single selection - replace entire array
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-start gap-2">
            <UserMinus className="h-5 w-5 text-orange-600" />
            Substitute Out <span className="truncate overflow-hidden">{performerToSubOut.name}</span>
          </DialogTitle>
          <DialogDescription>
            {eligibleReplacements.length > 0 ? (
              "Choose someone to replace them with, or substitute them out without a replacement."
            ) : (
              "No eligible replacements available. This performer will be substituted out without replacement."
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Hidden focusable element to prevent input auto-focus */}
          <button className="sr-only" aria-hidden="true" />
          

          {/* Replacement Selection */}
          {eligibleReplacements.length > 0 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  <Users className="h-4 w-4 inline mr-1" />
                  Choose replacement (optional)
                </label>
                <div className="relative">
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={() => setShowDropdown(true)}
                      onClick={() => setShowDropdown(true)}
                      placeholder="Search for someone to replace them..."
                      className="pr-10"
                    />
                    {selectedReplacementId && (
                      <button
                        onClick={handleClearSelection}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Dropdown Results */}
                  {showDropdown && (
                    <div 
                      className={`absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border ${
                        isChrome || isMobileSafari
                          ? 'bg-white border-gray-200' 
                          : 'bg-white/20 backdrop-blur-xl border-white/30'
                      }`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        backgroundColor: (isChrome || isMobileSafari) ? 'white' : undefined,
                        backdropFilter: (isChrome || isMobileSafari) ? 'none' : undefined
                      }}
                    >
                      {filteredReplacements.map((performer) => (
                        <div
                          key={performer.id}
                          onClick={() => handleSelectReplacement(performer)}
                          className="px-4 py-3 hover:bg-white/40 cursor-pointer border-b border-white/30 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">{performer.name}</span>
                            <div className="flex items-center space-x-2">
                              {performer.instruments?.length ? (
                                <Badge variant="outline" className="text-xs">
                                  {performer.instruments.join(", ")}
                                </Badge>
                              ) : (
                                <span className="text-xs text-slate-400">No instruments</span>
                              )}
                              {performer.priority && (
                                <Badge className="bg-orange-100 text-orange-800 text-xs">
                                  Priority
                                </Badge>
                              )}
                              {performer.played ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  Played
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600 text-xs">
                                  Not Played
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {filteredReplacements.length === 0 && searchTerm.trim().length > 0 && (
                        <div className="px-4 py-3 text-slate-500 text-sm">
                          No participants found matching "{searchTerm}"
                        </div>
                      )}
                      {eligibleReplacements.length === 0 && searchTerm.trim().length === 0 && (
                        <div className="px-4 py-3 text-slate-500 text-sm">
                          No eligible participants available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instrument Selection */}
          {selectedReplacement && selectedReplacement.instruments && selectedReplacement.instruments.length > 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  <Music className="h-4 w-4 inline mr-1" />
                  Select instrument for {selectedReplacement.name} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedReplacement.instruments.map((instrument) => (
                    <div key={instrument} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`instrument-${instrument}`}
                        name="replacement-instrument"
                        value={instrument}
                        checked={selectedInstruments.includes(instrument)}
                        onChange={() => handleInstrumentSelect(instrument)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label
                        htmlFor={`instrument-${instrument}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {instrument}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedInstruments.length === 0 && (
                  <div className="flex items-center space-x-2 mt-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Please select an instrument to continue</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <MutationButton 
              onClick={handleConfirm} 
              disabled={
                !!(selectedReplacementId && 
                selectedReplacement && 
                selectedReplacement.instruments && 
                selectedReplacement.instruments.length > 1 && 
                selectedInstruments.length === 0)
              }
              className="bg-orange-600/80 hover:bg-orange-700/90 border border-orange-500/30 disabled:bg-gray-400 disabled:cursor-not-allowed"
              style={{
                boxShadow: '0 4px 16px rgba(251,146,60,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
              glassMorphism={false}
            >
              {selectedReplacementId ? "Sub Out & Replace" : "Sub Out Without Replacement"}
            </MutationButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 